// A completed film is terminal: never seek, reload or cover its final logo.
export function manageHeroPlayback(video, showFallback) {
  const noProgressMs = 12000;
  let disposed = false;
  let completed = false;
  let status = 'loading';
  let pending = false;
  let attempt = 0;
  let retries = 0;
  let reloaded = false;
  let resumeTime = null;
  let lastTime = video.currentTime;
  let inView = true;
  let wasActive = !document.hidden;
  let lastProgressAt = Date.now();
  let timer;
  const failedSources = new Set();
  const sources = Array.from(video.querySelectorAll('source'));
  const listeners = [];
  const listen = (target, event, handler) => {
    target.addEventListener(event, handler);
    listeners.push(() => target.removeEventListener(event, handler));
  };
  const clearTimer = () => window.clearTimeout(timer);
  const finish = () => {
    completed = true;
    status = 'completed';
    clearTimer();
    showFallback(false);
  };
  const active = () => {
    if (disposed || completed) return false;
    if (video.ended) { finish(); return false; }
    return !document.hidden && inView;
  };
  const fail = (next = 'failed') => {
    if (disposed || completed || video.ended) return;
    status = next;
    clearTimer();
    showFallback(true);
  };
  const arm = () => {
    clearTimer();
    if (!active() || status === 'blocked' || status === 'failed' || status === 'offline') return;
    timer = window.setTimeout(recover, Math.max(0, noProgressMs - (Date.now() - lastProgressAt)));
  };
  const play = (retry = false) => {
    if (!active() || pending) return;
    if (retry && retries >= 3) { fail(); return; }
    if (retry) retries += 1;
    pending = true;
    const id = ++attempt;
    arm();
    const rejected = error => {
      if (disposed || completed || id !== attempt) return;
      pending = false;
      if (error?.name === 'NotAllowedError') fail('blocked');
      else if (error?.name === 'NotSupportedError') fail();
      else arm(); // Includes an interrupted play() while data is still arriving.
    };
    try {
      Promise.resolve(video.play()).then(() => {
        if (disposed || completed || id !== attempt) return;
        pending = false;
        // The playing event, rather than a resolved promise, removes the poster.
      }, rejected);
    } catch (error) { rejected(error); }
  };
  function recover(useAlternative = false) {
    if (!active() || status === 'blocked') return;
    if (navigator.onLine === false) { fail('offline'); return; }
    if (reloaded || retries >= 3) { fail(); return; }
    reloaded = true;
    resumeTime = video.currentTime;
    failedSources.clear();
    attempt += 1; // Ignore the AbortError from the interrupted old request.
    pending = false;
    status = 'loading';
    lastProgressAt = Date.now();
    showFallback(true);
    if (useAlternative) {
      const alternative = sources.find(source => source.type === 'video/webm' && video.canPlayType(source.type));
      if (alternative) video.src = alternative.getAttribute('src');
    }
    video.load(); // At most one resource reload per mounted film.
    play(true);
  }
  const visibleAgain = () => {
    const nowActive = active();
    if (nowActive && !wasActive) lastProgressAt = Date.now();
    wasActive = nowActive;
    if (!nowActive || status === 'blocked' || status === 'failed' || status === 'offline') { clearTimer(); return; }
    if (video.paused) play(true);
    arm();
  };
  listen(video, 'ended', finish);
  listen(video, 'playing', () => {
    if (!active()) return;
    status = 'playing';
    retries = 0;
    lastTime = video.currentTime;
    lastProgressAt = Date.now();
    showFallback(false);
    arm();
  });
  listen(video, 'timeupdate', () => {
    if (video.currentTime > lastTime) { lastTime = video.currentTime; lastProgressAt = Date.now(); arm(); }
  });
  for (const event of ['loadeddata', 'canplay']) listen(video, event, () => {
    if (!active() || status === 'blocked' || status === 'failed') return;
    if (video.paused) play(true);
    arm();
  });
  listen(video, 'loadedmetadata', () => {
    if (disposed || completed || video.ended || resumeTime === null) return;
    if (Number.isFinite(video.duration) && resumeTime > 0) {
      video.currentTime = Math.min(resumeTime, Math.max(0, video.duration - 1 / 24));
      lastTime = video.currentTime;
    }
    resumeTime = null;
  });
  for (const event of ['waiting', 'stalled']) listen(video, event, () => {
    if (!active() || status === 'blocked' || status === 'failed') return;
    status = 'buffering';
    arm();
  });
  listen(video, 'error', () => {
    if (!active()) return;
    if (video.error?.code === 2 || video.error?.code === 3) recover(video.error.code === 3);
    else fail();
  });
  for (const source of sources) listen(source, 'error', () => {
    if (disposed || completed || video.ended) return;
    failedSources.add(source);
    if (failedSources.size === sources.length) fail();
  });
  listen(document, 'visibilitychange', visibleAgain);
  listen(window, 'pageshow', visibleAgain);
  listen(window, 'focus', visibleAgain);
  listen(window, 'online', () => {
    if (!active() || completed || status === 'blocked') return;
    if ((status === 'failed' || status === 'offline') && !reloaded) recover();
    else visibleAgain();
  });
  for (const event of ['pointerdown', 'keydown']) listen(document, event, () => {
    if (status === 'blocked' && active()) play(true);
  });
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    inView = entries[0].isIntersecting;
    visibleAgain();
  }) : null;
  observer?.observe(video);
  video.muted = true;
  video.defaultMuted = true;
  play();
  return () => {
    disposed = true;
    attempt += 1;
    clearTimer();
    observer?.disconnect();
    listeners.forEach(remove => remove());
  };
}
